import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Container,
  Typography,
  Divider,
  Grid,
  TextField,
  Snackbar,
  Alert,
  Box,
  MenuItem,
  Autocomplete,
  InputLabel,
  FormControl,
  Select,
  FormHelperText
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import { Link } from 'react-router-dom';
import ShareIcon from '@mui/icons-material/Share';
import LocalHospital from '@mui/icons-material/LocalHospital';
import { API_BASE_URL } from '../../lib/constants'; // Ensure you have a config file for constants
import theme from '../../theme/theme';
import { ThemeProvider } from '@mui/material/styles';
import { useNotification } from '../../contexts/NotificationContext';
import DraftSaveButton from '../../components/DraftSaveButton';
import { DRAFT_KEYS, hasDraft, loadDraft } from '../../utils/draftUtils';
const PostOperativeTable = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { uhid_no } = useParams(); // Extract uhid_no directly from params
  const [existingAssessmentId, setExistingAssessmentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [patientData, setPatientData] = useState({
    ip_no: "",
    uhid_no: uhid_no || "",
    patient_name: "",
    age: "",
    sex: "",
    ward_no: "",
    room_no: "",
    assessment_date: "",
    anesthesiologist: "",
    surgeon: "",
    surgical_procedure: "",
    perioperative_analgesia_plan: "",
    preoperative: "",
    intraoperative: "",
    postoperative: ""
  });

  const [columns, setColumns] = useState([
    "Time (Hours)",
    "Pulse Rate",
    "Blood Pressure",
    "Respiratory Rate",
    "Oxygen Saturation",
    "VAS Pain Score",
    "Sedation Level",
    "Rescue Analgesia",
    "Staff Nurse"
  ]);
  const fetchPatientData = async (uhidNo) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/get_patient/${uhidNo}`);

      console.log(response.data);
      setPatientData(prev => ({
        ...prev,

        patient_name: response.data.name,
        age: response.data.age,
        sex: response.data.sex,



      }));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching patient data:', error);
      setLoading(false);
    }
  };
  const [rows, setRows] = useState([
    {
      id: 1,
      assessment_time: "",
      pulse_rate: "",
      blood_pressure: "",
      respiratory_rate: "",
      spo2: "",
      vas_score: "",
      sedation: "",
      rescue_analgesia_given: "",
      staff_nurse: ""
    }
  ]);

  const [submitStatus, setSubmitStatus] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  // Auto-load draft if present on mount
  useEffect(() => {
    if (uhid_no && hasDraft(DRAFT_KEYS.POST_OPERATIVE_ANALGESIA_ASSESSMENT, uhid_no)) {
      const saved = loadDraft(DRAFT_KEYS.POST_OPERATIVE_ANALGESIA_ASSESSMENT, uhid_no);
      if (saved && saved.formData) {
        // Saved draft contains patientData and assessment_rows
        const { assessment_rows: savedRows, ...pd } = saved.formData;
        setPatientData(prev => ({ ...prev, ...pd }));
        if (Array.isArray(savedRows) && savedRows.length > 0) {
          setRows(savedRows);
        }
      }
    }
  }, [uhid_no]);

  const handlePatientDataChange = (e) => {
    const { name, value } = e.target;
    setPatientData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };
  const handleNameChange = (e) => {
    const regex = /^[a-zA-Z\s.-]*$/; // allow letters, spaces, dot, hyphen
    const value = e.target.value;
    if (value === '' || regex.test(value)) {
      setPatientData({ ...patientData, [e.target.name]: value });
      if (errors[e.target.name]) {
        setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
      }
    }
  };

  const addRow = () => {
    const newRowId = rows.length > 0
      ? Math.max(...rows.map(r => r.id)) + 1
      : 1;

    setRows([
      ...rows,
      {
        id: newRowId,
        assessment_time: "",
        pulse_rate: "",
        blood_pressure: "",
        respiratory_rate: "",
        spo2: "",
        vas_score: "",
        sedation: "",
        rescue_analgesia_given: "",
        staff_nurse: ""
      }
    ]);
  };

  const deleteRow = (id) => {
    if (rows.length > 1) {
      setRows(rows.filter(row => row.id !== id));
    } else {
      setSubmitStatus({
        open: true,
        message: "At least one row must remain.",
        severity: "warning"
      });
    }
  };

  const updateRowValue = (rowId, field, value) => {
    const newRows = rows.map(row =>
      row.id === rowId ? { ...row, [field]: value } : row
    );
    setRows(newRows);
  };
  const [errors, setErrors] = useState({});

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case 'assessment_date':
        if (!value) error = "Assessment date is required";
        break;
      case 'surgical_procedure':
        if (!value || !value.trim()) error = "Surgical procedure is required";
        break;
      case 'room_no':
        if (!value) error = "Room number is required";
        break;
      case 'ward_no':
        if (!value) error = "Ward number is required";
        break;
      case 'anesthesiologist':
        if (!value) error = "Anaesthesiologist is required";
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
    return error === "";
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    validateField(name, value);
  };
  const validate = () => {
    const requiredFields = [
      { key: 'assessment_date',     label: 'Assessment Date'    },
      { key: 'surgical_procedure',  label: 'Surgical Procedure' },
      { key: 'room_no',             label: 'Room No.'           },
      { key: 'ward_no',             label: 'Ward No.'           },
      { key: 'anesthesiologist',    label: 'Anaesthesiologist'  },
    ];
    const tempErrors = {};
    requiredFields.forEach(({ key, label }) => {
      if (!patientData[key] || String(patientData[key]).trim() === '')
        tempErrors[key] = `${label} is required`;
    });
    setErrors(tempErrors);
    if (Object.keys(tempErrors).length > 0) {
      setTimeout(() => {
        const firstKey = Object.keys(tempErrors)[0];
        const el = document.querySelector(`[name="${firstKey}"]`);
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
      }, 100);
      showNotification('error', requiredFields.filter(f => tempErrors[f.key]).map(f => f.label));
    }
    return Object.keys(tempErrors).length === 0;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    // Check if the data to be sent is correct
    console.log("Data to be sent:", {
      ...patientData,
      assessment_rows: rows
    });

    const payload = {
      ...patientData,
      assessment_rows: rows
    };

    try {
      let response;
      if (existingAssessmentId) {
        // If we have an existing assessment ID, update it
        response = await axios.put(`${API_BASE_URL}/update_assessment/${existingAssessmentId}`, payload);
        showNotification('success', "Post-operative Analgesia Assessment Chart Form updated successfully!");
        console.log('Update response:', response.data);
      }

      else {
        // Otherwise create a new assessment
        response = await axios.post(`${API_BASE_URL}/submit_assessment`, payload);
        showNotification('success', "Post-operative Analgesia Assessment Chart Form submitted successfully!");
        console.log('Create response:', response.data);
      }

      setSubmitStatus({
        open: true,
        message: existingAssessmentId
          ? 'POST OPERATIVE - ANALGESIA ASSESSMENT CHART updated successfully!'
          : 'POST OPERATIVE - ANALGESIA ASSESSMENT CHART created successfully!',
        severity: 'success'
      });

      clearSavedData();
      navigate(`/mainPage/${uhid_no}`, {
        state: { from: 'PostOperativePhase', status: 'success', step: 3 },
      });
    } catch (error) {
      console.error("Error during submission:", error);
      setSubmitStatus({
        open: true,
        message: error.response?.data?.error || "Submission failed",
        severity: "error"
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSubmitStatus(prev => ({ ...prev, open: false }));
  };

  const fetchData = async () => {
    if (!uhid_no) {
      console.log("No UHID provided, skipping data fetch");
      return;
    }

    try {
      console.log("Fetching data for UHID:", uhid_no);
      const response = await axios.get(`${API_BASE_URL}/get_assessment/${uhid_no}`);
      console.log("API Response Data:", response.data);

      if (response.data && response.data.assessments && response.data.assessments.length > 0) {
        // Get the most recent assessment (first in the array since they're sorted DESC)
        const latestAssessment = response.data.assessments[0];

        console.log("Latest assessment found:", latestAssessment);

        // Set the existing assessment ID for updates
        setExistingAssessmentId(latestAssessment.id);

        // Map the main assessment data
        setPatientData({
          ip_no: latestAssessment.ip_no || "",
          uhid_no: latestAssessment.uhid_no || uhid_no,
          patient_name: latestAssessment.patient_name || "",
          age: latestAssessment.age || "",
          sex: latestAssessment.sex || "",
          ward_no: latestAssessment.ward_no || "",
          room_no: latestAssessment.room_no || "",
          assessment_date: latestAssessment.assessment_date
            ? new Date(latestAssessment.assessment_date).toISOString().split('T')[0]
            : '',
          anesthesiologist: latestAssessment.anesthesiologist || "",
          surgeon: latestAssessment.surgeon || "",
          surgical_procedure: latestAssessment.surgical_procedure || "",
          perioperative_analgesia_plan: latestAssessment.perioperative_analgesia_plan || "",
          preoperative: latestAssessment.preoperative || "",
          intraoperative: latestAssessment.intraoperative || "",
          postoperative: latestAssessment.postoperative || ""
        });

        // Map the assessment rows only if no draft exists
        if (!hasDraft(DRAFT_KEYS.POST_OPERATIVE_ANALGESIA_ASSESSMENT, uhid_no) && latestAssessment.assessment_rows && latestAssessment.assessment_rows.length > 0) {
          // Add an id property for frontend tracking if not present
          const mappedRows = latestAssessment.assessment_rows.map((row, index) => ({
            id: row.id || index + 1,
            assessment_time: row.assessment_time || "",
            pulse_rate: row.pulse_rate || "",
            blood_pressure: row.blood_pressure || "",
            respiratory_rate: row.respiratory_rate || "",
            spo2: row.spo2 || "",
            vas_score: row.vas_score || "",
            sedation: row.sedation || "",
            rescue_analgesia_given: row.rescue_analgesia_given || "",
            staff_nurse: row.staff_nurse || ""
          }));
          setRows(mappedRows);
        }
      } else {
        console.log("No assessment data found, initializing new form with UHID:", uhid_no);
        // Initialize a new form with just the UHID
        setPatientData(prevData => ({
          ...prevData,
          uhid_no: uhid_no,

        }));
        setExistingAssessmentId(null);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response && error.response.status === 404) {
        console.log("No records found, initializing new form");
        // Set just the UHID in case of a 404
        setPatientData(prevData => ({
          ...prevData,
          uhid_no: uhid_no,

        }));
      }
      setExistingAssessmentId(null);
    }
  };

  // Fetch next UHID & IP_No on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [ipRes] = await Promise.all([

          axios.get(`${API_BASE_URL}/assessment_next_ip`)
        ]);
        setPatientData(prev => ({
          ...prev,

          ip_no: ipRes.data.next_ip_no
        }));
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
      }
    };

    fetchInitialData();
  }, []);
  const [savedData, setSavedData] = useState(null);
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [currentPage, setCurrentPage] = useState('form');
  const [dataLoaded, setDataLoaded] = useState(false);
  // Auto-save functionality
  // Auto-save functionality - only after initial data is loaded
  useEffect(() => {
    if (!dataLoaded) return; // Don't auto-save until initial data is loaded

    const timeoutId = setTimeout(() => {
      if (hasFormData()) {
        saveFormData(patientData);
        setShowSaveNotification(true);
        setTimeout(() => setShowSaveNotification(false), 2000);
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [patientData, dataLoaded]);

  // Save form data to sessionStorage
  const saveFormData = (data) => {
    sessionStorage.setItem(`post_opertive_anal_ass_chartForm_${uhid_no}`, JSON.stringify(data));
    console.log('Form data saved:', data);
  };

  // Get saved form data
  const getSavedFormData = () => {
    const saved = sessionStorage.getItem(`post_opertive_anal_ass_chartForm_${uhid_no}`);
    return saved ? JSON.parse(saved) : null;
  };
  useEffect(() => {
    const saved = getSavedFormData();
    if (saved && Object.keys(saved).length > 0) {
      setSavedData(saved);
      setPatientData(saved);
    }
  }, []);
  // Check if form has any data
  const hasFormData = () => {
    return Object.values(patientData).some(value => {
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some(subValue =>
          typeof subValue === 'string' ? subValue.trim() !== '' : subValue
        );
      }
      return value === true;
    });
  };

  // Clear saved data - ONLY called on successful submit
  const clearSavedData = () => {
    setSavedData(null);
    sessionStorage.removeItem(`post_opertive_anal_ass_chartForm_${uhid_no}`);
    sessionStorage.removeItem(`formStatus_post_opertive_anal_ass_chart_${uhid_no}`);
  };

  // Home button handler
  const handleHomeNavigation = () => {
    if (hasFormData()) {
      saveFormData(patientData);
      // Store form status for home page to show "Continue" button
      sessionStorage.setItem(`formStatus_post_opertive_anal_ass_chart_${uhid_no}`, JSON.stringify({
        formName: 'POST OPERATIVE - ANALGESIA ASSESSMENT CHART',
        uhid_no: uhid_no,
        status: 'incomplete',
        lastSaved: new Date().toISOString(),
        continueUrl: `/postOperativeAssesment/${uhid_no}`
      }));
    }
    navigate('/home');
  };
  const handleBack = () => {
    navigate(-1); // Navigate to the previous page
  };
  useEffect(() => {
    fetchData();
    fetchPatientData(uhid_no);
  }, [uhid_no]);
  const roomOptions = ["101", "102", "103", "201", "202", "203"];
  const wardOptions = ['Ward 1', 'Ward 2', 'ICU', 'Recovery'];
  const anesthesiologistOptions = ['Dr. A Kumar', 'Dr. B Sharma', 'Dr. C Patel'];
  const procedureOptions = ['Appendectomy', 'Hernia Repair', 'Cataract Surgery'];
  const preoperativeOptions = ['Paracetamol', 'NSAIDs', 'None'];
  const intraoperativeOptions = ['Regional block', 'IV opioids', 'Local infiltration'];
  const postoperativeOptions = ['Tramadol', 'Paracetamol', 'Morphine'];
  const handleShareClick = async () => {
    try {
      // Call API to generate a share token for the given UHID
      const res = await axios.post(`${API_BASE_URL}/generate_share_token/${uhid_no}`);

      const token = res.data.token;
      const link = `${API_BASE_URL}/postOperativeAssesment/${uhid_no}?token=${token}`;
      const message = `Dear Doctor,\n\nPlease fill the post-operative analgesia chart:\n\n${link}`;

      // Copy the shareable link to clipboard
      await navigator.clipboard.writeText(link);

      // Show success notification to user
      setSubmitStatus({
        open: true,
        message: "Secure link copied to clipboard!",
        severity: "info",
      });

      // Open WhatsApp chat with pre-filled message (replace phone number if needed)
      const phoneNumber = '+916379605575'; // no spaces or special chars for wa.me
      window.open(`http://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    } catch (error) {
      console.error("Error generating share token:", error);

      // Show error notification to user
      setSubmitStatus({
        open: true,
        message: "Failed to generate shareable link",
        severity: "error",
      });
    }
  };

  const goToVideoRecorder = (formName, uhid_no) => {
    navigate(`/video?form=${encodeURIComponent(formName)}&uhid_no=${encodeURIComponent(uhid_no)}`);
  };
  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 3 }}>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
            {/* Heading with Icon */}
            <Typography
              variant="h4"
              sx={{
                color: '#008080',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >

              POST OPERATIVE - ANALGESIA
              <br></br>ASSESSMENT CHART
            </Typography>

            {/* Right-aligned Logo and Text */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography
                variant="h6"
                sx={{
                  color: '#008080',
                  marginLeft: 1,
                  fontWeight: 500,
                  letterSpacing: 0.3,
                }}
              >
                OTMS
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            {/* Left-aligned Back Button */}
            <Button
              variant="outlined"
              component={Link}
              to={`/mainPage/${uhid_no}`}
              state={{ from: 'PostOperativePhase', status: 'success', step: 3 }}
              sx={{ marginLeft: 2 }}
            >
              Back
            </Button>

            {/* Right-aligned Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleHomeNavigation}
                sx={{ marginRight: 1 }}
              >
                Home
              </Button>
              <Button
                variant="contained"
                onClick={() => goToVideoRecorder(" POST OPERATIVE ANALGESIA ASSESSMENT CHART", uhid_no)}
                sx={{ marginRight: 1 }}
              >
                Add Iterative Video
              </Button>
              <IconButton color="primary" aria-label="share" onClick={handleShareClick}>
                <ShareIcon />
              </IconButton>
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth
                label="Patient Name"
                name="patient_name"
                InputProps={{ readOnly: true, }}
                value={patientData.patient_name}
                onChange={handleNameChange}
                variant="outlined"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="UHID No."
                name="uhid_no"
                value={patientData.uhid_no}

                onChange={handlePatientDataChange}
                variant="outlined"
                required
                InputProps={{ readOnly: true, }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth label=" IP No."
                name="ip_no"
                value={patientData.ip_no}
                onChange={handlePatientDataChange}
                InputProps={{ readOnly: true, }}
                variant="outlined"
                required

              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                variant="outlined"
                required
                error={Boolean(errors.assessment_date)}
                helperText={errors.assessment_date}
                value={patientData.assessment_date}
                onChange={handlePatientDataChange}
                onBlur={handleBlur}
                name="assessment_date"
                type="date"
                inputProps={{
                  min: new Date().toISOString().slice(0, 10)
                }}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" required error={!!errors.room_no}>
                <InputLabel id="room-label">Room No.</InputLabel>
                <Select
                  labelId="room-label"
                  id="room-select"
                  name="room_no"
                  value={patientData.room_no || ''}
                  onChange={(e) => {
                    setPatientData({ ...patientData, room_no: e.target.value });
                    if (errors.room_no) setErrors(prev => ({ ...prev, room_no: "" }));
                  }}
                  onBlur={handleBlur}
                  label="Room No."
                >
                  {roomOptions.map((room, index) => (
                    <MenuItem key={index} value={room}>
                      {room}
                    </MenuItem>
                  ))}
                </Select>
                {errors.room_no && <FormHelperText>{errors.room_no}</FormHelperText>}
              </FormControl>

            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Gender" InputProps={{ readOnly: true, }} name="sex" required fullWidth onChange={(e) => {
                const { name, value } = e.target;
                setPatientData((prev) => ({ ...prev, [name]: value }));
                if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
              }} value={patientData.sex}>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" required error={!!errors.ward_no}>
                <InputLabel id="ward-label">Ward No.</InputLabel>
                <Select
                  labelId="ward-label"
                  id="ward-select"
                  name="ward_no"
                  value={patientData.ward_no || ''}
                  onChange={(e) => {
                    setPatientData({ ...patientData, ward_no: e.target.value });
                    if (errors.ward_no) setErrors(prev => ({ ...prev, ward_no: "" }));
                  }}
                  onBlur={handleBlur}
                  label="Ward No."
                >
                  {wardOptions.map((ward, index) => (
                    <MenuItem key={index} value={ward}>
                      {ward}
                    </MenuItem>
                  ))}
                </Select>
                {errors.ward_no && <FormHelperText>{errors.ward_no}</FormHelperText>}
              </FormControl>

            </Grid>


          </Grid>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" required error={!!errors.anesthesiologist}>
                <InputLabel id="anaesthesiologist-label">Anaesthesiologist</InputLabel>
                <Select
                  labelId="anaesthesiologist-label"
                  id="anaesthesiologist-select"
                  name="anesthesiologist"
                  value={patientData.anesthesiologist || ''}
                  onChange={(e) => {
                    setPatientData({ ...patientData, anesthesiologist: e.target.value });
                    if (errors.anesthesiologist) setErrors(prev => ({ ...prev, anesthesiologist: "" }));
                  }}
                  onBlur={handleBlur}
                  label="Anaesthesiologist"
                >
                  {anesthesiologistOptions.map((doctor, index) => (
                    <MenuItem key={index} value={doctor}>
                      {doctor}
                    </MenuItem>
                  ))}
                </Select>
                {errors.anesthesiologist && <FormHelperText>{errors.anesthesiologist}</FormHelperText>}
              </FormControl>

            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                freeSolo
                options={procedureOptions}
                value={patientData.surgical_procedure || ""}
                onInputChange={(e, newValue) => {
                  setPatientData((prev) => ({ ...prev, surgical_procedure: newValue }));
                  if (errors.surgical_procedure) setErrors(prev => ({ ...prev, surgical_procedure: "" }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Surgical Procedure done" variant="outlined" required error={!!errors.surgical_procedure} helperText={errors.surgical_procedure} onBlur={handleBlur} name="surgical_procedure" />
                )}
              />

            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" >Surgeon</Typography>
          <TextField fullWidth variant="outlined"
            name="surgeon"
            value={patientData.surgeon}
            onChange={handlePatientDataChange}
            multiline rows={3} />
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6">Plan of Perioperative Analgesia</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                freeSolo
                options={preoperativeOptions}
                value={patientData.preoperative || ""}
                onInputChange={(e, newValue) =>
                  setPatientData((prev) => ({ ...prev, preoperative: newValue }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Preoperative" variant="outlined" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                freeSolo
                options={intraoperativeOptions}
                value={patientData.intraoperative || ""}
                onInputChange={(e, newValue) =>
                  setPatientData((prev) => ({ ...prev, intraoperative: newValue }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Intraoperative" variant="outlined" />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                freeSolo
                options={postoperativeOptions}
                value={patientData.postoperative || ""}
                onInputChange={(e, newValue) =>
                  setPatientData((prev) => ({ ...prev, postoperative: newValue }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Postoperative (Preemptive analgesia prescribed)"
                    variant="outlined"
                  />
                )}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <TableContainer component={Paper} sx={{ marginTop: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((col, index) => (
                    <TableCell key={index}>{col}</TableCell>
                  ))}
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    {[
                      "assessment_time",
                      "pulse_rate",
                      "blood_pressure",
                      "respiratory_rate",
                      "spo2",
                      "vas_score",
                      "sedation",
                      "rescue_analgesia_given",
                      "staff_nurse"
                    ].map((field, colIndex) => (
                      <TableCell key={`${row.id}-${colIndex}`}>
                        {field === "assessment_time" ? (
                          <TextField
                            fullWidth
                            variant="standard"
                            type="time" // Change the input type to time
                            value={row[field]}
                            onChange={(e) => updateRowValue(row.id, field, e.target.value)}
                          />
                        ) : (
                          <TextField
                            fullWidth
                            variant="standard"
                            value={row[field]}
                            onChange={(e) => updateRowValue(row.id, field, e.target.value)}
                          />
                        )}
                      </TableCell>
                    ))}
                    <TableCell>
                      <IconButton
                        onClick={() => deleteRow(row.id)}
                        color="error"
                        disabled={rows.length <= 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Button
              variant="contained"
              onClick={addRow}
              startIcon={<AddCircleIcon />}
              sx={{ margin: 2 }}
            >
              Add Row
            </Button>
          </TableContainer>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, marginTop: 3 }}>
              <DraftSaveButton
                formKey={DRAFT_KEYS.POST_OPERATIVE_ANALGESIA_ASSESSMENT}
                uhidNo={uhid_no}
                formData={{ ...patientData, assessment_rows: rows }}
                onDraftLoaded={(draftData) => {
                  const { assessment_rows, ...patientData } = draftData;
                  setPatientData(prev => ({ ...prev, ...patientData }));
                  if (assessment_rows) setRows(assessment_rows);
                }}
                variant="outlined"
                size="large"
                disabled={loading}
                isUpdate={existingAssessmentId}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleSubmit}
                sx={{ marginTop: 3 }}
              >
                {existingAssessmentId ? "Update" : "Submit"}
              </Button>
            </Box></Grid>
        </Paper>
        <Snackbar
          open={submitStatus.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={submitStatus.severity}
            sx={{ width: '100%' }}
          >
            {submitStatus.message}
          </Alert>
        </Snackbar>
      </Container>
    </ThemeProvider>
  );
}

export default PostOperativeTable;
